import { createApp } from '@/createApp';
import { httpService } from '@/services/HttpService';
import { useSyncHtmlLang } from '@/composables/useSyncHtmlLang';
import { useAuthStore } from '@/stores/auth';
import { setSiteSettings } from '@/modules/seo/siteSettings';
import type { SeoSiteSettings } from '@/modules/seo/siteSettings';
import type { LinkPageHost } from '@/modules/link-page/domain/entities/LinkPage';
import '@site/assets/main.css';
import '@/lib/ui/theme.css';

type InitialState = {
  pinia?: Record<string, unknown>;
  locale?: string;
  siteSettings?: Partial<SeoSiteSettings>;
  linkPage?: LinkPageHost | null;
};

declare global {
  interface Window {
    __INITIAL_STATE__?: InitialState;
    __SSR_HYDRATING__?: boolean;
  }
}

export async function hydrate() {
  // The parent reads this callback URL; mounting the embedded app would restore shared credentials.
  if (window.location.pathname === '/auth/callback' && window.parent !== window) {
    return;
  }

  httpService.init();
  window.__SSR_HYDRATING__ = true;

  const initialState = window.__INITIAL_STATE__ ?? {};
  setSiteSettings(initialState.siteSettings ?? {});
  const { app, router, pinia } = createApp(window.location.pathname + window.location.search, initialState.locale, initialState.linkPage);

  if (initialState.pinia) {
    pinia.state.value = initialState.pinia as typeof pinia.state.value;
  }

  await router.isReady();

  app.mount('#app', true);
  useSyncHtmlLang();

  window.__SSR_HYDRATING__ = false;

  const authStore = useAuthStore();
  if (!authStore.token && typeof httpService.getAuthToken === 'function') {
    void authStore.loadTokenFromStorage();
  }
}

void hydrate();
